import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Copy, RefreshCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FilterX } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

// ─── Badge colors — must stay in sync with AdminTypes.tsx BADGE_COLORS ───────

const BADGE_COLORS: Record<string, { label: string; cls: string }> = {
  rose:   { label: 'Vermell', cls: 'bg-rose-100 text-rose-700' },
  purple: { label: 'Morat',   cls: 'bg-purple-100 text-purple-700' },
  blue:   { label: 'Blau',    cls: 'bg-blue-100 text-blue-700' },
  green:  { label: 'Verd',    cls: 'bg-green-100 text-green-700' },
  amber:  { label: 'Ambre',   cls: 'bg-amber-100 text-amber-700' },
  teal:   { label: 'Teal',    cls: 'bg-teal-100 text-teal-700' },
  orange: { label: 'Taronja', cls: 'bg-orange-100 text-orange-700' },
  slate:  { label: 'Gris',    cls: 'bg-slate-100 text-slate-700' },
};

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface NodeTypeDef {
  code: string;
  name: string;
  badgeColor: string;
  isActive: number;
}

interface CompositeCodeOption {
  id: number;
  code: string;
  name: string;
}

interface Node {
  id: string;
  code?: string;
  name: string;
  nodeType: 'COMPOSITE' | 'ESCANDALL';
  unit: string;
  amidament?: number;
  indirectCostPercentage: number;
  cachedTotalMaterialsCost: number;
  cachedTotalLaborOwnCost: number;
  cachedTotalLaborSubcontractCost: number;
  cachedTotalServicesCost: number;
  cachedTotalOtherCost: number;
  cachedTotalDirectCost: number;
  cachedTotalIndirectCost: number;
  cachedTotalCost: number;
  preuVenda?: number;
  cachedTotalWeightKg: number;
  cachedTotalHoursOwn: number;
  cachedTotalHoursSubcontract: number;
  isSemiFinished: number;
  isActive: number;
}

// Metriques derivades per fila, mateixa base de calcul que Ratis/Dades Economiques a NodeForm
// (que les calcula igual per a Escandalls i Compostos — preuVenda es desa per a tots dos tipus).
function nodeMetrics(node: Node) {
  const amidament = Number(node.amidament) || 0;
  const ingresTotal = Number(node.preuVenda) || 0;
  const costTotal = Number(node.cachedTotalCost) || 0;
  const costCompres = Number(node.cachedTotalMaterialsCost) + Number(node.cachedTotalServicesCost) + Number(node.cachedTotalIndirectCost);
  const valorAfegit = ingresTotal - costCompres;
  const totalHores = Number(node.cachedTotalHoursOwn) + Number(node.cachedTotalHoursSubcontract);
  const pes = Number(node.cachedTotalWeightKg) || 0;
  return {
    costTotalUA:  amidament > 0 ? costTotal / amidament : null,
    ingresTotalUA: amidament > 0 ? ingresTotal / amidament : null,
    factorMarge:  costTotal > 0 ? ingresTotal / costTotal : null,
    preuHora:     totalHores > 0 ? valorAfegit / totalHores : null,
    horesUA:      amidament > 0 ? totalHores / amidament : null,
    preuKg:       pes > 0 ? ingresTotal / pes : null,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt2 = (v: number | undefined) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Component ────────────────────────────────────────────────────────────────

export default function Nodes() {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch]                 = useSessionState('nodes:search', '');
  const [nodeTypeFilter, setNodeTypeFilter] = useSessionState('nodes:nodeTypeFilter', '');
  const [compositeCodeFilter, setCompositeCodeFilter] = useSessionState('nodes:compositeCodeFilter', '');
  const [compositeSubfamilyFilter, setCompositeSubfamilyFilter] = useSessionState('nodes:compositeSubfamilyFilter', '');
  const [compositeProductFilter, setCompositeProductFilter] = useSessionState('nodes:compositeProductFilter', '');
  const [compositeModelFilter, setCompositeModelFilter] = useSessionState('nodes:compositeModelFilter', '');
  const [compositeVariantFilter, setCompositeVariantFilter] = useSessionState('nodes:compositeVariantFilter', '');
  const [compositeCodes, setCompositeCodes] = useState<CompositeCodeOption[]>([]);
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<{ id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean }[]>([]);
  const [compositeModels, setCompositeModels] = useState<{ id: number; code: string; name?: string | null; productId: number; isActive: number | boolean }[]>([]);
  const [compositeVariants, setCompositeVariants] = useState<{ id: number; code: string; name?: string | null; modelId: number; isActive: number | boolean }[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('code');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [nodeTypeMap, setNodeTypeMap] = useState<Record<string, NodeTypeDef>>({});

  useEffect(() => {
    api.get<{ success: boolean; data: NodeTypeDef[] }>('/catalog/node-types')
      .then(res => {
        const map: Record<string, NodeTypeDef> = {};
        res.data.forEach(nt => { map[nt.code] = nt; });
        setNodeTypeMap(map);
      })
      .catch(() => {});
    api.get<{ success: boolean; data: CompositeCodeOption[] }>('/nodes/composite-codes')
      .then(res => setCompositeCodes(res.data ?? []))
      .catch(() => {});
    api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies')
      .then(res => setCompositeSubfamilies((res.data ?? []).filter((s) => Number(s.isActive) === 1)))
      .catch(() => {});
    api.get<{ success: boolean; data: typeof compositeProducts }>('/catalog/composite-products')
      .then(res => setCompositeProducts((res.data ?? []).filter((p) => Number(p.isActive) === 1)))
      .catch(() => {});
    api.get<{ success: boolean; data: typeof compositeModels }>('/catalog/composite-models')
      .then(res => setCompositeModels((res.data ?? []).filter((m) => Number(m.isActive) === 1)))
      .catch(() => {});
    api.get<{ success: boolean; data: typeof compositeVariants }>('/catalog/composite-variants')
      .then(res => setCompositeVariants((res.data ?? []).filter((v) => Number(v.isActive) === 1)))
      .catch(() => {});
  }, []);

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortBy(col); setSortDir('ASC'); }
    setPage(1);
  };
  const sortIcon = (col: string) => sortBy === col
    ? <span className="ml-1">{sortDir === 'ASC' ? '↑' : '↓'}</span>
    : <span className="ml-1 text-muted-foreground/40">↕</span>;

  const fetchNodes = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { limit, page, sortBy, sortDir };
      if (search) params.search = search;
      if (nodeTypeFilter) {
        params.nodeType = nodeTypeFilter;
      }
      if (compositeCodeFilter) {
        params.compositeCodeId = compositeCodeFilter;
      }
      if (compositeSubfamilyFilter) {
        params.compositeSubfamilyId = compositeSubfamilyFilter;
      }
      if (compositeProductFilter) {
        params.compositeProductId = compositeProductFilter;
      }
      if (compositeModelFilter) {
        params.compositeModelId = compositeModelFilter;
      }
      if (compositeVariantFilter) {
        params.compositeVariantId = compositeVariantFilter;
      }
      const response = await api.get<{
        success: boolean;
        data: Node[];
        pagination: { total: number; totalPages: number; page: number };
      }>('/nodes', params);
      setNodes(response.data);
      setTotal(response.pagination?.total ?? 0);
      setTotalPages(response.pagination?.totalPages ?? 1);
      setError(null);
    } catch (err) {
      setError('Error en carregar els nodes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, nodeTypeFilter, compositeCodeFilter, compositeSubfamilyFilter, compositeProductFilter, compositeModelFilter, compositeVariantFilter, limit, page, sortBy, sortDir]);

  useEffect(() => {
    fetchNodes();
  }, [fetchNodes]);

  const handleDelete = async (id: string) => {
    if (!confirm('Estàs segur que vols eliminar aquest node?')) return;
    try {
      await api.delete(`/nodes/${id}`);
      fetchNodes();
    } catch {
      setError('Error en eliminar');
    }
  };

  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const handleDuplicate = async (id: string) => {
    setDuplicatingId(id);
    try {
      const res = await api.post<{ success: boolean; data: { id: string } }>(`/nodes/${id}/duplicate`);
      if (res.success) navigate(`/compres/nodes/${res.data.id}/edit`);
    } catch {
      setError('Error en duplicar');
    } finally {
      setDuplicatingId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      <Card className="rounded-xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              title="Netejar tots els filtres"
              onClick={() => { setSearch(''); setNodeTypeFilter(''); setCompositeCodeFilter(''); setCompositeSubfamilyFilter(''); setCompositeProductFilter(''); setCompositeModelFilter(''); setCompositeVariantFilter(''); setPage(1); }}
            >
              <FilterX className="h-4 w-4" />
            </Button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                autoFocus
                placeholder="Cercar per codi o nom..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
              />
            </div>
            <ExcelFilterDropdown
              label="Tipus"
              options={Object.values(nodeTypeMap).map(nt => ({ value: nt.code, label: nt.name }))}
              selected={nodeTypeFilter ? nodeTypeFilter.split(',') : []}
              onApply={(values) => { setNodeTypeFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Codi producte"
              options={compositeCodes.map(cc => ({ value: String(cc.id), label: `${cc.code} - ${cc.name}` }))}
              selected={compositeCodeFilter ? compositeCodeFilter.split(',') : []}
              onApply={(values) => { setCompositeCodeFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Subfamília"
              options={compositeSubfamilies
                .filter((s) => !compositeCodeFilter || compositeCodeFilter.split(',').includes(String(s.compositeCodeId)))
                .map((s) => ({ value: String(s.id), label: s.name }))}
              selected={compositeSubfamilyFilter ? compositeSubfamilyFilter.split(',') : []}
              onApply={(values) => { setCompositeSubfamilyFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Producte"
              options={compositeProducts
                .filter((p) => !compositeSubfamilyFilter || compositeSubfamilyFilter.split(',').includes(String(p.subfamilyId)))
                .map((p) => ({ value: String(p.id), label: p.name }))}
              selected={compositeProductFilter ? compositeProductFilter.split(',') : []}
              onApply={(values) => { setCompositeProductFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Model"
              options={compositeModels
                .filter((m) => !compositeProductFilter || compositeProductFilter.split(',').includes(String(m.productId)))
                .map((m) => ({ value: String(m.id), label: m.name || m.code }))}
              selected={compositeModelFilter ? compositeModelFilter.split(',') : []}
              onApply={(values) => { setCompositeModelFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Variant"
              options={compositeVariants
                .filter((v) => !compositeModelFilter || compositeModelFilter.split(',').includes(String(v.modelId)))
                .map((v) => ({ value: String(v.id), label: v.name || v.code }))}
              selected={compositeVariantFilter ? compositeVariantFilter.split(',') : []}
              onApply={(values) => { setCompositeVariantFilter(values.join(',')); setPage(1); }}
            />
            <Button variant="outline" size="sm" onClick={fetchNodes}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => navigate('/compres/nodes/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Nou Escandall
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregant...</div>
          ) : nodes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No hi ha escandalls</div>
          ) : (
            <>
            {/* Cabecera de columnas */}
            <div className="flex items-center px-4 py-1 border-b text-xs text-muted-foreground select-none gap-2">
              <span className="w-4" />
              <button onClick={() => toggleSort('code')} className="w-28 shrink-0 text-left flex items-center text-xs hover:text-foreground transition-colors">
                Codi{sortIcon('code')}
              </button>
              <button onClick={() => toggleSort('name')} className="flex-1 text-left flex items-center text-xs hover:text-foreground transition-colors">
                Nom{sortIcon('name')}
              </button>
              <span className="w-24 text-center">Tipus</span>
              <button onClick={() => toggleSort('amidament')} className="w-24 justify-center flex items-center text-xs hover:text-foreground transition-colors">
                Amidament{sortIcon('amidament')}
              </button>
              <button onClick={() => toggleSort('costTotalUA')} className="w-24 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Cost Total / Amidament">
                Cost Total/ua{sortIcon('costTotalUA')}
              </button>
              <button onClick={() => toggleSort('ingresTotalUA')} className="w-24 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Ingrés Total / Amidament">
                Ingrés Total/ua{sortIcon('ingresTotalUA')}
              </button>
              <button onClick={() => toggleSort('factorMarge')} className="w-20 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Ingrés Total / Cost Total">
                Factor Marge{sortIcon('factorMarge')}
              </button>
              <button onClick={() => toggleSort('preuHora')} className="w-20 justify-center flex items-center text-xs hover:text-foreground transition-colors">
                Preu hora{sortIcon('preuHora')}
              </button>
              <button onClick={() => toggleSort('horesUA')} className="w-16 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Hores / Amidament">
                Hores/ua{sortIcon('horesUA')}
              </button>
              <button onClick={() => toggleSort('kg')} className="w-16 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Pes total">
                Kg{sortIcon('kg')}
              </button>
              <button onClick={() => toggleSort('preuKg')} className="w-20 justify-center flex items-center text-xs hover:text-foreground transition-colors" title="Ingrés Total / Pes total">
                Preu Kg{sortIcon('preuKg')}
              </button>
              <span className="w-24 ml-3" />
            </div>
            <div className="space-y-1 mt-1">
              {nodes.map((node) => {
                const ntDef = nodeTypeMap[node.nodeType];
                const badgeColor = BADGE_COLORS[ntDef?.badgeColor ?? ''] ?? BADGE_COLORS.slate;
                const badgeLabel = ntDef
                  ? ntDef.name + (node.isSemiFinished ? ' > Semi elaborat' : '')
                  : node.nodeType;
                const badge = { label: badgeLabel, className: badgeColor.cls };
                const m = nodeMetrics(node);
                return (
                  <div key={node.id} className="border rounded-lg">
                    <div
                      className="flex items-center px-4 py-3 cursor-pointer hover:bg-slate-50 text-[12px] text-muted-foreground gap-2"
                      onClick={() => navigate(`/compres/nodes/${node.id}/edit`)}
                    >
                      <span
                        className="w-4 shrink-0 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === node.id ? null : node.id); }}
                      >
                        {expandedId === node.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                      <span className="w-28 shrink-0 font-mono truncate">{node.code || '—'}</span>
                      <span className="flex-1 font-medium truncate">{node.name}</span>
                      <span className="w-24 shrink-0 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>{badge.label}</span>
                      </span>
                      <span className="w-24 shrink-0 text-center">{fmt2(node.amidament ?? 1)} {node.unit || 'Ut'}</span>
                      <span className="w-24 shrink-0 text-center font-semibold">{m.costTotalUA != null ? `${fmt2(m.costTotalUA)} €` : '—'}</span>
                      <span className="w-24 shrink-0 text-center">{m.ingresTotalUA != null ? `${fmt2(m.ingresTotalUA)} €` : '—'}</span>
                      <span className="w-20 shrink-0 text-center">{m.factorMarge != null ? fmt2(m.factorMarge) : '—'}</span>
                      <span className="w-20 shrink-0 text-center">{m.preuHora != null ? `${fmt2(m.preuHora)} €/h` : '—'}</span>
                      <span className="w-16 shrink-0 text-center">{m.horesUA != null ? `${fmt2(m.horesUA)} h` : '—'}</span>
                      <span className="w-16 shrink-0 text-center">{node.cachedTotalWeightKg ? `${fmt2(node.cachedTotalWeightKg)} kg` : '—'}</span>
                      <span className="w-20 shrink-0 text-center">{m.preuKg != null ? `${fmt2(m.preuKg)} €` : '—'}</span>
                      <div className="w-24 shrink-0 flex justify-end items-center gap-0.5 border-l border-slate-200 pl-3 ml-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/compres/nodes/${node.id}/edit`); }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDuplicate(node.id); }}
                          disabled={duplicatingId === node.id}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-40"
                          title="Duplicar"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(node.id); }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {expandedId === node.id && (
                      <div className="border-t p-4 bg-slate-50">
                        <div className="grid grid-cols-4 gap-4 text-[12px]">
                          <div><span className="text-slate-500">Materials:</span> {fmt2(node.cachedTotalMaterialsCost)} &euro;</div>
                          <div><span className="text-slate-500">M.O. Pròpia:</span> {fmt2(node.cachedTotalLaborOwnCost)} &euro;</div>
                          <div><span className="text-slate-500">M.O. Subcontrac.:</span> {fmt2(node.cachedTotalLaborSubcontractCost)} &euro;</div>
                          <div><span className="text-slate-500">Serveis:</span> {fmt2(node.cachedTotalServicesCost)} &euro;</div>
                          <div><span className="text-slate-500">Altres:</span> {fmt2(node.cachedTotalOtherCost)} &euro;</div>
                          <div><span className="text-slate-500">Directe:</span> {fmt2(node.cachedTotalDirectCost)} &euro;</div>
                          <div>
                            <span className="text-slate-500">Indirecte ({Number(node.indirectCostPercentage || 0).toFixed(1)}%):</span>{' '}
                            {fmt2(node.cachedTotalIndirectCost)} &euro;
                          </div>
                          <div className="font-semibold">
                            <span className="text-slate-700">Total:</span> {fmt2(node.cachedTotalCost)} &euro;
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Mostrar</span>
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="text-xs border border-input rounded px-2 py-1 bg-background focus:outline-none"
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
              <span className="text-xs text-muted-foreground">per pàgina · {total} escandalls</span>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Pàgina {page} de {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
